import { useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { DialogContentSafeArea } from '@/components/ui/dialog-safe-area';
import { PostPreviewLayout, ExportPostPreviewDialog, type PostData } from '@/components/post-preview';
import { createTransformedContent } from '../../utils/post-transformers';
import { useDisplayMode } from 'react-openai-apps-sdk';
import { useOpenAIActions } from 'react-openai-apps-sdk';
import type { Post } from '../../types';

interface PostPreviewDialogProps {
  selectedPost: Post | null;
  selectedPostIndex: number | null;
  sortedPostsLength: number;
  onNavigate: (direction: 'prev' | 'next') => void;
  onClose: () => void;
  onUpdatePost: (updatedPost: Post) => void;
  onSchedule?: (post: Post) => void;
}

export function PostPreviewDialog({
  selectedPost,
  selectedPostIndex,
  sortedPostsLength,
  onNavigate,
  onClose,
  onUpdatePost,
  onSchedule,
}: PostPreviewDialogProps) {
  const displayMode = useDisplayMode();
  const { sendFollowUpMessage } = useOpenAIActions();
  const [exportData, setExportData] = useState<{ url: string; filename: string } | null>(null);

  if (!selectedPost) return null;

  const handlePostUpdate = (updates: Partial<PostData>) => {
    const updatedPost: Post = {
      ...selectedPost,
      ...updates as Partial<Post>,
    };
    onUpdatePost(updatedPost);
  };

  const handleSchedule = (post: PostData) => {
    if (onSchedule) {
      onSchedule(post as Post);
    }
  };

  const handleExportComplete = (url: string, filename: string) => {
    setExportData({ url, filename });
  };

  const handleSendViaEmail = async () => {
    if (!exportData?.url) return;
    await sendFollowUpMessage({
      prompt: `Send this post preview image via email: ${exportData.url}`,
    });
  };

  const transformedContent = createTransformedContent(selectedPost) as any;

  return (
    <>
    <Dialog open={!!selectedPost} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContentSafeArea className="flex flex-col p-0 max-h-[85vh] overflow-y-auto [scrollbar-gutter:stable] [&>button]:hidden gap-0">
        <PostPreviewLayout
          post={selectedPost as PostData}
          onPostUpdate={handlePostUpdate}
          transformedContent={transformedContent}
          displayMode={(displayMode || 'inline') as 'inline' | 'fullscreen' | 'pip'}
          showNavigation={true}
          onNavigatePrev={() => onNavigate('prev')}
          onNavigateNext={() => onNavigate('next')}
          hasPrev={selectedPostIndex !== null && selectedPostIndex > 0}
          hasNext={selectedPostIndex !== null && selectedPostIndex < sortedPostsLength - 1}
          onSchedule={onSchedule ? handleSchedule : undefined}
          onClose={onClose}
          onExportComplete={handleExportComplete}
        />
      </DialogContentSafeArea>
    </Dialog>

    <ExportPostPreviewDialog
      open={!!exportData}
      onClose={() => setExportData(null)}
      uploadedUrl={exportData?.url || null}
      filename={exportData?.filename || null}
      onSendViaEmail={handleSendViaEmail}
    />
    </>
  );
}
